import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api, type Me } from './api';
import { LoginPage } from './pages/LoginPage';
import { PortalPage } from './pages/PortalPage';
import { AdminPage } from './pages/AdminPage';

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; me: Me };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => !cancelled && setAuth({ status: 'signed-in', me }))
      .catch((err) => {
        if (cancelled) return;
        if ((err as { status?: number }).status === 401) {
          setAuth({ status: 'signed-out' });
        } else {
          setAuth({ status: 'signed-out' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function signOut() {
    await api.logout().catch(() => undefined);
    setAuth({ status: 'signed-out' });
  }

  if (auth.status === 'loading') {
    return (
      <div className="grid min-h-screen place-items-center paper-grain bg-ink">
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="grid h-12 w-12 place-items-center rounded-sm bg-coral text-ink">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 2C8 2 5 6 5 11s3 11 7 11 7-6 7-11-3-9-7-9Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M12 2v20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          <p className="font-mono text-[10px] uppercase tracking-widestest text-cream-500">
            Setting the type · please wait
          </p>
        </div>
      </div>
    );
  }

  if (auth.status === 'signed-out') {
    if (location.pathname !== '/login') {
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
    }
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/" element={<PortalPage me={auth.me} onSignOut={signOut} />} />
      <Route
        path="/admin"
        element={
          auth.me.isAdmin ? (
            <AdminPage me={auth.me} onSignOut={signOut} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
